import { useState, useEffect } from 'react';
import useAuth from '../../../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUserProfile, setUserProfile } from '../../../../store/slices/authSlice';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import { showError } from '../../../../utils/toast';
import ClinicProfileSideBar from './sidebar';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

export default function ClinicProfile() {
    const currentProfileData = useSelector(selectCurrentUserProfile);
    const [profileData, setProfileData] = useState(currentProfileData);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['clinic'] });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { setIsLoading } = useLoading();
    const MySwal = withReactContent(Swal);

    const fetchClinicData = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/details`);
            if (response?.data?.result === 'success') {
                let clinic_details = response.data.data.clinic_details;
                let user_details = response.data.data.user_details;
                let data = {
                    profile_pic: clinic_details?.profile_picture || null,
                    clinic_name: clinic_details?.clinic_name || '',
                    owner_name: clinic_details?.owner_name || '',
                    owner_name_prefix: clinic_details?.owner_name_prefix || '',
                    registration_number: clinic_details?.registration_number || '',
                    contact_number_code: clinic_details?.contact_number_code || '',
                    contact_number: clinic_details?.contact_number || '',
                    address_line1: clinic_details?.address_line1 || '',
                    address_line2: clinic_details?.address_line2 || '',
                    address_district: clinic_details?.address_district || '',
                    address_state: clinic_details?.address_state || '',
                    address_country: clinic_details?.address_country || 'India',
                    address_pincode: clinic_details?.address_pincode || '',
                    email: clinic_details?.email || '',
                    bank_name: clinic_details?.bank_name || '',
                    bank_account_number: clinic_details?.bank_account_number || '',
                    bank_ifsc_code: clinic_details?.bank_ifsc_code || '',
                    pan_card_number: clinic_details?.pan_card_number || '',

                    user_name: user_details?.name || '',
                    user_phone_code: user_details?.phone_code || '',
                    user_phone_number: user_details?.phone_number || '',
                    user_email: user_details?.email || ''
                };
                setProfileData(data);
                dispatch(setUserProfile({
                    name: user_details?.name || '',
                    email: user_details?.email || '',
                    phone_code: user_details?.phone_code || '',
                    phone_number: user_details?.phone_number || '',
                    profile_pic: clinic_details?.profile_picture || '',
                }));
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        document.title = 'Varosa - Clinic Profile';
        fetchClinicData();
    }, []);

    const deleteAccountPopup = () => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to delete your account?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                deleteAccount();
            }
        });
    };
    const deleteAccount = async () => {
        try {
            setIsLoading(true);
            const response = await api.post('/clinic/profile/delete');
            if (response?.data?.result === 'success') {
                showError('Account deleted successfully!');
                navigate('/login');
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="container-fluid contact py-5">
                <div className="container py-5">
                    <div className="row g-4">
                        <div className="col-lg-4 col-xl-4 contact-form wow fadeInLeft" data-wow-delay="0.1s">
                            <ClinicProfileSideBar user={user} role={userRole} profileData={profileData} />
                        </div>
                        <div className="col-lg-8 col-xl-8 contact-form wow fadeInLeft" data-wow-delay="0.1s">
                            <div className="card">
                                <h5 className="card-header p-3">
                                    My Profile Information
                                </h5>
                                <div className="card-body p-3">
                                    <p className="card-text">
                                        <b>Clinic Name:</b> {profileData?.clinic_name}<br /><br />
                                        <b>Owner Name:</b> {profileData?.owner_name_prefix} {profileData?.owner_name}<br /><br />
                                        <b>Registration Number:</b> {profileData?.registration_number}<br /><br />
                                        <b>Contact Number:</b> {profileData?.contact_number_code} {profileData?.contact_number}<br /><br />
                                        <b>Address:</b> {profileData?.address_line1}, {profileData?.address_line2}, {profileData?.address_district}, {profileData?.address_state}, {profileData?.address_country}, {profileData?.address_pincode}<br /><br />
                                        <b>Email:</b> {profileData?.email}<br /><br />
                                        <b>Bank Name:</b> {profileData?.bank_name}<br /><br />
                                        <b>Bank Account Number:</b> {profileData?.bank_account_number}<br /><br />
                                        <b>Bank IFSC Code:</b> {profileData?.bank_ifsc_code}<br /><br />
                                        <b>PAN Card Number:</b> {profileData?.pan_card_number}<br /><br />
                                    </p>
                                </div>
                                <div className="card-footer text-muted p-3">
                                    <Link to="/dashboard/clinic/profile/edit" className="btn btn-primary float-start">Edit Profile</Link>
                                    <button onClick={deleteAccountPopup} className="btn btn-danger ms-2 float-end">Delete Account</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}